import { useState } from 'react'
import { SkeletonList } from '../componentes/Skeleton'
import { EstadoError } from '../componentes/EstadoError'
import { EstadoVacio } from '../componentes/EstadoVacio'
import { TablaDatos, ayudanteColumnas } from '../componentes/TablaDatos'
import { IconoCliente } from '../componentes/iconos'
import { TAMANO_PAGINA, useClientes, type ClienteResumen } from '../hooks/useClientes'
import { useFiltrosCatalogo, type OpcionFiltro } from '../hooks/useFiltrosCatalogo'
import { useFiltrosUrl } from '../hooks/useFiltrosUrl'
import { BarraFiltros } from './logistica/BarraFiltros'
import { GraficaProductos } from './logistica/GraficaProductos'
import { PanelCliente } from './logistica/PanelCliente'
import { TarjetaCliente } from './logistica/TarjetaCliente'

const ayudante = ayudanteColumnas<ClienteResumen>()

const COLUMNAS = ayudante.columns([
  ayudante.accessor('nombre', { header: 'Nombre' }),
  ayudante.accessor((fila) => fila.movil ?? '', { id: 'movil', header: 'Movil' }),
  ayudante.accessor((fila) => fila.direccion ?? '', { id: 'direccion', header: 'Direccion' }),
  ayudante.accessor((fila) => fila.ciudad ?? '', { id: 'ciudad', header: 'Poblacion' }),
  ayudante.accessor((fila) => fila.codigo_postal ?? '', { id: 'codigo_postal', header: 'C.P.' }),
])

function etiquetaDe(opciones: OpcionFiltro[], clave: string): string {
  return opciones.find((opcion) => opcion.clave === clave)?.etiqueta ?? clave
}

function Metrica({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="rounded-xl border border-borde bg-superficie px-4 py-3">
      <dt className="text-xs font-semibold uppercase tracking-wide text-tinta-apagada">{etiqueta}</dt>
      <dd className="mt-1 text-2xl font-semibold text-tinta">{valor}</dd>
    </div>
  )
}

export function Logistica() {
  const { filtros, cambiarFiltro, irAPagina } = useFiltrosUrl()
  const clientes = useClientes(filtros)
  const catalogo = useFiltrosCatalogo()
  // Se guarda la fila entera, no solo el id: asi el panel conserva el nombre
  // aunque un cambio de filtro saque a ese cliente de la pagina actual.
  const [seleccionado, setSeleccionado] = useState<ClienteResumen | null>(null)

  const semanas = catalogo.data?.semanas ?? []
  const rutas = catalogo.data?.rutas ?? []

  const filas = clientes.data?.items ?? []
  // El total es el que devuelve la api, no `filas.length`: con 200 por pagina
  // contar filas mentiria en cuanto haya mas de una pagina.
  const total = clientes.data?.total ?? 0
  const tamanoPagina = clientes.data?.page_size || TAMANO_PAGINA
  const pagina = clientes.data?.page ?? filtros.page
  const paginas = Math.max(1, Math.ceil(total / tamanoPagina))

  const activos: string[] = []
  if (filtros.q) activos.push(`busqueda "${filtros.q}"`)
  if (filtros.semana) activos.push(etiquetaDe(semanas, filtros.semana))
  if (filtros.ruta) activos.push(etiquetaDe(rutas, filtros.ruta))

  const estadoVacio = (
    <EstadoVacio
      titulo="No hay clientes con estos filtros"
      descripcion={
        activos.length > 0
          ? `Ningun cliente coincide con los filtros activos: ${activos.join(', ')}.`
          : 'El listado ha llegado vacio de la API.'
      }
      accion={
        filtros.q ? (
          <button
            type="button"
            onClick={() => cambiarFiltro('q', undefined)}
            className="rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm font-medium text-tinta-2 transition-colors hover:bg-elevado"
          >
            Limpiar la busqueda
          </button>
        ) : null
      }
    />
  )

  return (
    <div className="flex flex-col gap-4">
      {/* En movil, con el detalle abierto, la cabecera cede la pantalla al
          panel; en escritorio se queda siempre. */}
      <header className={seleccionado ? 'hidden lg:block' : 'block'}>
        <h1 className="text-xl font-semibold text-tinta">Logistica</h1>
        <p className="mt-1 text-sm text-tinta-apagada">
          Reparto por semana y ruta. Selecciona un cliente para ver su ficha y su ubicacion.
        </p>
        <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Metrica etiqueta="Clientes" valor={String(total)} />
          <Metrica etiqueta="En esta pagina" valor={String(filas.length)} />
          <Metrica etiqueta="Pagina" valor={`${pagina} de ${paginas}`} />
        </dl>
      </header>

      {/* Dos columnas fijas en escritorio: la tabla no cambia de ancho al abrir
          o cerrar el panel, asi que nada se mueve bajo el raton. */}
      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_24rem]">
        <section
          aria-label="Listado de clientes"
          className={`flex min-w-0 flex-col gap-4 ${seleccionado ? 'hidden lg:flex' : 'flex'}`}
        >
          <BarraFiltros
            filtros={filtros}
            semanas={semanas}
            rutas={rutas}
            onCambiar={cambiarFiltro}
          />

          {clientes.isPending ? (
            <SkeletonList />
          ) : clientes.isError ? (
            <EstadoError error={clientes.error} onReintentar={() => void clientes.refetch()} />
          ) : filas.length === 0 ? (
            estadoVacio
          ) : (
            <>
              {/* Tabla solo en escritorio. */}
              <div className="hidden lg:block">
                <TablaDatos
                  datos={filas}
                  columnas={COLUMNAS}
                  onSeleccionar={setSeleccionado}
                  seleccionadoId={seleccionado?.id ?? null}
                  estadoVacio={null}
                  etiqueta="Clientes del reparto"
                />
              </div>

              {/* Tarjetas apiladas en movil. */}
              <ul className="flex flex-col gap-2 lg:hidden">
                {filas.map((cliente) => (
                  <TarjetaCliente
                    key={cliente.id}
                    cliente={cliente}
                    seleccionado={seleccionado?.id === cliente.id}
                    onSeleccionar={setSeleccionado}
                  />
                ))}
              </ul>

              <nav
                aria-label="Paginacion"
                className="flex items-center justify-between gap-3 text-sm"
              >
                <button
                  type="button"
                  aria-label="Pagina anterior"
                  disabled={pagina <= 1}
                  onClick={() => irAPagina(pagina - 1)}
                  className="rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 font-medium text-tinta-2 transition-colors hover:bg-elevado disabled:cursor-not-allowed disabled:border-borde disabled:text-desactivado-tinta"
                >
                  Anterior
                </button>
                <span className="text-tinta-apagada">
                  Pagina {pagina} de {paginas}
                </span>
                <button
                  type="button"
                  aria-label="Pagina siguiente"
                  disabled={pagina >= paginas}
                  onClick={() => irAPagina(pagina + 1)}
                  className="rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 font-medium text-tinta-2 transition-colors hover:bg-elevado disabled:cursor-not-allowed disabled:border-borde disabled:text-desactivado-tinta"
                >
                  Siguiente
                </button>
              </nav>
            </>
          )}
        </section>

        <div className={`lg:sticky lg:top-4 ${seleccionado ? 'block' : 'hidden lg:block'}`}>
          {seleccionado ? (
            // La clave reinicia el estado local del panel (factura pedida,
            // error de geocodificacion) al cambiar de cliente.
            <PanelCliente
              key={seleccionado.id}
              cliente={seleccionado}
              onVolver={() => setSeleccionado(null)}
            />
          ) : (
            <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-borde-fuerte bg-superficie px-6 py-12 text-center">
              <IconoCliente aria-hidden="true" className="size-8 text-tinta-apagada" />
              <p className="text-sm text-tinta-apagada">
                Selecciona un cliente del listado para ver su ficha.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Fuera de la rejilla de dos columnas y de todo condicional: la grafica
          no depende de que haya un cliente seleccionado, ni de un filtro, ni
          del valor de retorno de un boton. Se monta una vez y se queda. */}
      <GraficaProductos />
    </div>
  )
}
